import { Component } from '@theme/component';

/**
 * Wholesale pricing calculator.
 *
 * Purely indicative - it quotes a price and sends the buyer to sales. Nothing
 * here touches the cart, so the numbers only ever have to agree with the rate
 * card, not with Shopify.
 *
 * The tier table is rendered into a JSON script tag by the section rather than
 * being fetched or hardcoded here, so a merchant editing prices in the theme
 * editor changes the maths without anyone touching this file.
 *
 * Two inputs (a range and a number box) drive one value. They're kept in sync
 * through a single `#setQuantity`, so neither can drift from the other, and
 * the number box is only clamped on `change`/blur - clamping on every
 * keystroke makes typing "50" impossible when the minimum is 15, because the
 * "5" gets corrected before the "0" arrives.
 *
 * @typedef {{ min: number, price: number, shipping: string }} Tier
 * @typedef {{ id: string, name: string, rrp: number, tiers: Tier[], customMin: number }} Product
 *
 * @typedef {Object} Refs
 * @property {HTMLScriptElement} data
 * @property {HTMLInputElement[]} [productInputs]
 * @property {HTMLInputElement} [range]
 * @property {HTMLInputElement} [number]
 * @property {HTMLElement} [total]
 * @property {HTMLElement} [unitPrice]
 * @property {HTMLElement} [finePrint]
 * @property {HTMLElement} [quoteBody]
 * @property {HTMLElement} [customBody]
 * @extends {Component<Refs>}
 */
export class WholesaleCalculatorComponent extends Component {
  requiredRefs = ['data'];

  /** @type {Product[]} */
  #products = [];

  /** @type {{ prefix: string, suffix: string, unitLabel: string, finePrint: string }} */
  #strings = { prefix: '$', suffix: '', unitLabel: 'per unit', finePrint: '' };

  #quantity = 0;

  connectedCallback() {
    super.connectedCallback();

    try {
      const parsed = JSON.parse(this.refs.data.textContent ?? '{}');
      this.#products = parsed.products ?? [];
      this.#strings = { ...this.#strings, ...(parsed.strings ?? {}) };
    } catch {
      // A malformed payload should leave the server-rendered markup alone
      // rather than blanking the panel.
      return;
    }

    if (!this.#products.length) return;

    this.#quantity = this.#clamp(Number(this.refs.range?.value ?? this.#min()));
    this.#render();
  }

  #min() {
    return Number(this.refs.range?.min ?? 1);
  }

  #max() {
    return Number(this.refs.range?.max ?? 100);
  }

  /** @param {number} value */
  #clamp(value) {
    if (!Number.isFinite(value)) return this.#min();
    return Math.min(this.#max(), Math.max(this.#min(), Math.round(value)));
  }

  /** @returns {Product | undefined} */
  #product() {
    const checked = (this.refs.productInputs ?? []).find((input) => input.checked);
    return this.#products.find((p) => p.id === checked?.value) ?? this.#products[0];
  }

  /**
   * The applicable tier is the last one whose minimum the quantity has reached.
   * @param {Product} product
   * @param {number} quantity
   * @returns {Tier | undefined}
   */
  #tierFor(product, quantity) {
    let match;
    for (const tier of product.tiers) {
      if (quantity >= tier.min) match = tier;
    }
    return match;
  }

  /**
   * @param {number} amount
   * @param {boolean} [withSuffix] - false for the big total, whose currency
   *   code lives in its own span so it can be styled separately. That span is
   *   static markup, so it never needs rewriting here.
   */
  #money(amount, withSuffix = true) {
    const { prefix, suffix } = this.#strings;
    const figure = amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${prefix}${figure}${withSuffix ? suffix : ''}`;
  }

  /**
   * @param {number} value
   * @param {'range' | 'number' | null} source - the input that already holds
   *   this value, so it isn't rewritten mid-edit.
   */
  #setQuantity(value, source) {
    this.#quantity = this.#clamp(value);
    const { range, number } = this.refs;
    if (range && source !== 'range') range.value = String(this.#quantity);
    if (number && source !== 'number') number.value = String(this.#quantity);
    this.#render();
  }

  #render() {
    const product = this.#product();
    if (!product) return;

    const qty = this.#quantity;
    const isCustom = product.customMin > 0 && qty >= product.customMin;
    const tier = this.#tierFor(product, qty);

    const { quoteBody, customBody, finePrint } = this.refs;
    const noQuote = isCustom || !tier;

    // Both panels exist in the markup; only one is shown, so switching between
    // a quoted price and "talk to us" needs no re-render of either.
    if (quoteBody) quoteBody.hidden = noQuote;
    if (customBody) customBody.hidden = !noQuote;

    // The fine print describes a tier, so it has to go with the quote. Left
    // visible it sat under "Custom pricing" still quoting the previous tier's
    // discount and shipping.
    if (finePrint) finePrint.hidden = noQuote;

    if (noQuote) return;

    const unit = tier.price;
    const total = unit * qty;
    const saved = product.rrp * qty - total;
    const percent = product.rrp > 0 ? Math.round((1 - unit / product.rrp) * 100) : 0;

    const { total: totalEl, unitPrice } = this.refs;
    if (totalEl) totalEl.textContent = this.#money(total, false);
    if (unitPrice) unitPrice.textContent = `${this.#money(unit)} ${this.#strings.unitLabel}`;

    // Deliberately kept out of the price block - it's fine print under the
    // call to action, not part of the number the buyer is reading.
    if (finePrint) {
      finePrint.textContent = this.#strings.finePrint
        .replace('[percent]', String(percent))
        .replace('[quantity]', String(qty))
        .replace('[amount]', this.#money(saved))
        .replace('[shipping]', tier.shipping ?? '');
    }
  }

  /** Fires from either product radio. */
  productChanged() {
    this.#render();
  }

  /** Fires while the slider is dragged. */
  rangeInput() {
    this.#setQuantity(Number(this.refs.range?.value), 'range');
  }

  /**
   * Fires on every keystroke in the number box. Deliberately does not clamp -
   * see the note at the top - it only previews values that are already valid.
   */
  numberInput() {
    const raw = Number(this.refs.number?.value);
    if (!Number.isFinite(raw) || this.refs.number?.value === '') return;
    if (raw < this.#min() || raw > this.#max()) return;
    this.#setQuantity(raw, 'number');
  }

  /** Fires on blur / enter, where correcting an out-of-range value is welcome. */
  numberCommit() {
    this.#setQuantity(Number(this.refs.number?.value), null);
  }
}

if (!customElements.get('wholesale-calculator-component')) {
  customElements.define('wholesale-calculator-component', WholesaleCalculatorComponent);
}
